/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
//@ts-nocheck
import {
  CalcGroup,
  Calculation,
  CalculationResult,
  CalculationRowType,
  CalculatorLogic,
  CumulativeCalculation,
  Dates,
  DatelyObject,
  FrameworkAgreement,
  Numeric,
  Objects,
  PaymentChannel,
  PensionCalculation,
  ReportType,
  TaxcardKind,
  TaxDeductionWorkCategories,
  UnionPaymentKind,
  UnionPaymentType,
  Unit,
  Years,
  YearlyChangingNumbers,
  YearlySideCosts,
  Translations,
  Language
} from "@salaxy/core";

import { CalculationReport } from "./CalculationReport";
import { CalculationReportImpl } from "./CalculationReportImpl";
import { CalculationReportRow } from "./CalculationReportRow";
import { CalculationReportRowGroup } from "./CalculationReportRowGroup";
import { CalculationReportSimpleRow } from "./CalculationReportSimpleRow";
import { CalculationSummaryReport } from "./CalculationSummaryReport";
import { CalculationTableRowData } from "./CalculationTableRowData";
import { PaymentTableRowData } from "./PaymentTableRowData";
import { HeaderFooterData, ReportOptions, ReportLayout } from "../../model";

/**
 * Maps Calculation-object to report template ready objects.
 */
export class CalculationMapper {

  /**
   * Creates a new CalculationReport from the fiven Calculation.
   * @param calc Calcuation for the report.
   * @param reportType Report type.
   */
  public static getCalculationReport(calc: Calculation, reportType: ReportType, lang: Language | undefined): CalculationReport | undefined {
    if (!calc) {
      return undefined;
    }
    // do not mutate...
    const calculation = Objects.copy(calc);
    // correct units for selected partners
    if (calculation.partner == "FI81POYS0016206791" ||
      calculation.employer?.avatar?.partner == "FI81POYS0016206791"
    ) {
      calculation.result?.irRows?.forEach(irRow => {
        if ((irRow.data ?? {}).calculationRowType != "Remuneration" &&
          (irRow.data ?? {}).calculationRowType != "HolidayBonus" &&
          (irRow.data ?? {}).calculationRowType != "HolidayCompensation") {
          if (irRow.unit == Unit.Percent) {
            irRow.unit = Unit.Count;
          }
        }
      });
    }
    const report = new CalculationReportImpl(reportType);

    this.createHeader(report, calculation);

    if (!calculation.result?.totals) {
      return report;
    }

    switch (reportType) {
      case ReportType.SalarySlip:
        this.createValidationMessages(report, calculation);
        this.createSalarySlipRowGroups(report, calculation, lang);
        this.createSalarySlipNotes(report, calculation);
        this.createCumulativeData(report, calculation);
        break;
      case ReportType.EmployerReport:
        this.createValidationMessages(report, calculation);
        this.createEmployerReportRowGroups(report, calculation, lang);
        this.createEmployerReportNotes(report, calculation);
        break;
      case ReportType.PaymentReport:
        this.createPaymentReportRowGroups(report, calculation, lang);
        break;
    }
    return report;
  }

  /**
   * Creates a new CalculationSummaryReport from the given calculations.
   * @param calcs Calculations for the report.
   * @param lang Language.
   */
  public static getCalculationSummaryReport(calcs: Calculation[], lang: Language): CalculationSummaryReport | undefined {
    if (!calcs) {
      return undefined;
    }

    const report: CalculationSummaryReport = { rowGroups: [], };
    const calcResultsSummary = this.calculateCalculationsResultsSummary(calcs);
    const summarizedCalc = CalculatorLogic.getBlank();
    summarizedCalc.result ??= {};
    summarizedCalc.result.employerCalc = calcResultsSummary.employerCalc;
    summarizedCalc.result.totals = calcResultsSummary.totals;
    summarizedCalc.result.workerCalc = calcResultsSummary.workerCalc;

    this.createSummaryHeader(report, calcResultsSummary, calcs);
    this.createPaymentReportRowGroups(report, summarizedCalc, lang);

    return report;
  }

/**
 * Creates HeaderFooterData from provided parameters
 * @param type ReportType
 * @param calc Calculation BusinessObject
 * @param options ReportOptions
 * @param lang gets translation with this lang, if null use current lang
 * @returns Created HeaderFooterData
 */
  public static getHeaderFooter(type: ReportType, calc: Calculation, options: ReportOptions, lang: Language | undefined = undefined): HeaderFooterData {
    return {
      logo: options?.logo,
      img2: options?.img2,
      title: type === ReportType.EmployerReport
        ? Translations.get("SALAXY.REPORTS.employer.title", null, lang)
        : type === ReportType.PaymentReport ? Translations.get("SALAXY.REPORTS.payment.title", null, lang) : Translations.get("SALAXY.REPORTS.salary.title", null, lang),
      subTitle: `${Translations.get("SALAXY.REPORTS.salary.subtitle", null, lang)}: ${Dates.getFormattedDate(calc.workflow?.salaryDate)}`,
      today: Dates.getFormattedDate(Dates.getToday()),
      date: Dates.getFormattedDate(calc.workflow?.salaryDate)
    };
  }

/**
 * Creates HeaderFooterData from provided parameters
 * @param calc Calculation BusinessObject
 * @param options ReportOptions
 * @param lang gets translation with this lang, if null use current lang
 * @returns Created HeaderFooterData
 */
   public static getTotalReportHeaderFooter(calc: Calculation | Calculation[], options: ReportOptions, lang: Language | undefined = undefined): HeaderFooterData {
    const bo = Array.isArray(calc) ? calc[0] : calc;
    return {
      logo: options?.logo,
      img2: options?.img2,
      title: Translations.get("SALAXY.ENUM.ReportType.totalsReport.label", null, lang),
      today: Dates.getFormattedDate(Dates.getToday()),
      date: Dates.getFormattedDate(bo.workflow?.salaryDate)
    };
  }

  /**
   * Get ReportLayout for calculation
   * @param isLandscape If true, the layout is rendered as landscape.
   * @param pdf By default the mode is htmlPage. Set this to true to use pdf mode. This may affect some rendering details.
   * @param watermark If provided, the watermark is shown in the report. Use "default" to show the default watermark ("SALAXY.REPORTS.document.preview").
   * @returns Report task
   */
  public static getReportLayout(isLandscape = false, pdf = false, watermark? :string): ReportLayout {
    const isPreview = !!watermark;
    return {
      isPreview,
      watermark: watermark == "default" ? "SALAXY.REPORTS.document.preview" : watermark,
      layout: isLandscape ? "a4Landscape" : "a4Portrait",
      isLayoutA4Portrait: !isLandscape,
      isLayoutA4Landscape: isLandscape,
      mode: pdf ? "pdf" : "htmlPage",
      isModeHtmlPage: !pdf,
      isModePdf: pdf,
    };
  }

/**
 * Creates HeaderFooterData (for summaryreport) from provided parameters
 * @param calcs Array of Calculation BusinessObjects
 * @param reportOptions ReportOptions
 * @param lang gets translation with this lang, if null use current lang
 * @returns Created HeaderFooterData
 */
  public static getCalculationSummaryReportHeaderFooter(calcs: Calculation[], reportOptions: ReportOptions, lang: Language): HeaderFooterData {
    const summary = this.getCalculationSummaryReport(calcs, lang);

    return {
      date: summary?.header?.salaryDate,
      title: Translations.get("SALAXY.REPORTS.payment.summaryTitle", null, lang),
      subTitle: `${Translations.get("SALAXY.REPORTS.payment.summarySubTitle", null, lang)}: ${Dates.getFormattedDate(summary?.header?.salaryDate)}`,
      today: Dates.getToday(),
      logo: reportOptions?.logo,
      img2: reportOptions?.img2
    };
  }

  private static createSalarySlipRowGroups(report: CalculationReport, calc: Calculation, lang: Language): void {
    // Ordered set of groups
    const groups: CalcGroup[] = [
      CalcGroup.BaseSalary,
      CalcGroup.SalaryAdditions,
      CalcGroup.OtherNoPayment,
      CalcGroup.Benefits,
      CalcGroup.Deductions,
      CalcGroup.Expenses,
      CalcGroup.Totals,
      // CalcGroup.Disabled,
    ];

    const rowGroups = groups.map((group) => {
      const irRows = calc.result?.irRows?.filter((r) => r.calcData?.grouping === group);
      return new CalculationReportRowGroup<CalculationReportRow<CalculationTableRowData>>(
        {
          grouping: group,
          rows: irRows?.map((i) => new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData({ irRow: i }, lang))),
        });
    });
    report.rowGroups?.push(...rowGroups);


    // Deductions
    const deductionGroup = rowGroups.find((g) => g.grouping === CalcGroup.Deductions);

    // HACK: Move prepaid expenses to expenses group
    const expensesGroup = rowGroups.find((g) => g.grouping === CalcGroup.Expenses);
    expensesGroup.rows.push(...deductionGroup.rows.filter((x) =>
      this.getEnum<CalculationRowType>(x.data.irRow.data.calculationRowType) ===
      CalculationRowType.PrepaidExpenses));

    deductionGroup.rows = deductionGroup.rows.filter((x) =>
      this.getEnum<CalculationRowType>(x.data.irRow.data.calculationRowType) !==
      CalculationRowType.PrepaidExpenses);

    for (const row of deductionGroup.rows) {

      const calculationRowType = this.getEnum<CalculationRowType>(row.data.irRow.data.calculationRowType);
      switch (calculationRowType) {
        case CalculationRowType.Advance:
          if (calc.result.workerCalc.fullSalaryAdvance > calc.result.workerCalc.salaryAdvance) {
            row.data.fullTotal = row.data.irRow.total;
            row.data.irRow.total = 0;
            row.data.isTotalReduced = true;
          }
          break;
        case CalculationRowType.UnionPayment:
          row.data.irRow.count = row.data.irRow.total / row.data.irRow.price;
          if (calc.result.workerCalc.fullUnionPayment > calc.result.workerCalc.unionPayment) {
            row.data.fullTotal = row.data.irRow.total;
            row.data.irRow.total = 0;
            row.data.isTotalReduced = true;
          }
          break;
        case CalculationRowType.TaxAtSource: {
          const taxAtSourceDecuction = calc.result.irRows.flat().find(x => x.irData.code === 405);
          if(taxAtSourceDecuction) {
            row.data.comment = `${Translations.get("SALAXY.REPORTS.salary.heading.taxAtSourceComment", null, lang)} ${Numeric.formatPrice(taxAtSourceDecuction.price)}`;
          }
          break;
        }
        default:
          if (calc.result.workerCalc.fullOtherDeductions > calc.result.workerCalc.otherDeductions) {
            if (row.data.irRow?.irData?.code >= 400 &&  !([402,412,413,414,417].indexOf(row.data.irRow.irData.code) >= 0) ) {
              row.data.fullTotal = row.data.irRow.total;
              row.data.irRow.total = 0;
              row.data.isTotalReduced = true;
            }
          }
          break;
      }
    }

    // Set prepaid expenses

    for (const row of expensesGroup.rows) {
      const calculationRowType = this.getEnum<CalculationRowType>(row.data.irRow.data.calculationRowType);
      switch (calculationRowType) {
        case CalculationRowType.PrepaidExpenses:
          row.data.irRow.price = -1 * row.data.irRow.price;
          row.data.irRow.total = -1 * row.data.irRow.total;
          if (calc.result.workerCalc.fullPrepaidExpenses > calc.result.workerCalc.prepaidExpenses) {
            row.data.fullTotal = row.data.irRow.total;
            row.data.irRow.total = 0;
            row.data.isTotalReduced = true;
          }
          break;
      }
    }

    // First deduct taxes
    deductionGroup.rows.unshift(
      // Tax
      new CalculationReportRow<CalculationTableRowData>(
        new CalculationTableRowData(
          {
            irRow:
            {
              message: "SALAXY.REPORTS.salary.heading.withholdingTax",
              count: calc.result.totals.totalTaxable !== 0
                ? calc.result.workerCalc.tax / calc.result.totals.totalTaxable
                : 0,
              price: calc.result.totals.totalTaxable,
              total: calc.result.workerCalc.tax,
              unit: Unit.Percent,
            },
            fullTotal: calc.result.workerCalc.fullTax,
            isTotalReduced: calc.result.workerCalc.fullTax > calc.result.workerCalc.tax,
          },
        )));

    deductionGroup.rows.push(
      // Pension
      new CalculationReportRow<CalculationTableRowData>(
        new CalculationTableRowData(
          {
            irRow:
            {
              message: "SALAXY.REPORTS.salary.heading.pensionPayment", //
              count: calc.result.totals.totalPensionInsuranceBase !== 0
                ? calc.result.workerCalc.pension /
                calc.result.totals.totalPensionInsuranceBase
                : 0,
              price: calc.result.totals.totalPensionInsuranceBase,
              total: calc.result.workerCalc.pension,
              unit: Unit.Percent,
            },
            comment: (calc.result.workerCalc.fullPension > 0
              ? this.getPensionCompanyName(calc)
              : ""),
            fullTotal: calc.result.workerCalc.fullPension,
            isTotalReduced: calc.result.workerCalc.fullPension >
              calc.result.workerCalc.pension,
          },
        )));
    deductionGroup.rows.push(
      // Unemployment
      new CalculationReportRow<CalculationTableRowData>(
        new CalculationTableRowData(
          {
            irRow:
            {
              message: "SALAXY.REPORTS.salary.heading.unemploymentInsurancePayment",
              count: calc.result.totals.totalUnemploymentInsuranceBase !== 0
                ? calc.result.workerCalc.unemploymentInsurance /
                calc.result.totals.totalUnemploymentInsuranceBase
                : 0,
              price: calc.result.totals.totalUnemploymentInsuranceBase,
              total: calc.result.workerCalc.unemploymentInsurance,
              unit: Unit.Percent,
            },
            fullTotal: calc.result.workerCalc.fullUnemploymentInsurance,
            isTotalReduced: calc.result.workerCalc.fullUnemploymentInsurance >
              calc.result.workerCalc.unemploymentInsurance,
          },
        )));

        if ((calc.result.workerCalc as any).fullSocialSecurity > 0) {
          deductionGroup.rows.push(
            // Worker's social security
            new CalculationReportRow<CalculationTableRowData>(
              new CalculationTableRowData(
                {
                  irRow:
                  {
                    message: "SALAXY.REPORTS.salary.heading.workersSocialInsurancePayment",
                    count: calc.result.totals.totalHealthInsuranceBase !== 0
                      ? (calc.result.workerCalc as any).socialSecurity /
                      calc.result.totals.totalHealthInsuranceBase
                      : 0,
                    price: calc.result.totals.totalHealthInsuranceBase,
                    total: (calc.result.workerCalc as any).socialSecurity,
                    unit: Unit.Percent,
                  },
                  fullTotal: (calc.result.workerCalc as any).fullSocialSecurity,
                    isTotalReduced: (calc.result.workerCalc as any).fullSocialSecurity >
                    (calc.result.workerCalc as any).socialSecurity,
                },
              )));
        }

    // Totals
    const totalsGroup = rowGroups.find((g) => g.grouping === CalcGroup.Totals);
    totalsGroup.rows.push(
      // Gross salary
      new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
        {
          irRow:
          {
            message: "SALAXY.REPORTS.ENUM.CalcGroup.baseSalary.label",
            total: calc.result.totals.totalGrossSalary,
          },
        })));
    totalsGroup.rows.push(
      // Deductions
      new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
        {
          irRow:
          {
            message: "SALAXY.REPORTS.ENUM.CalcGroup.deductions.label",
            total: -1 * calc.result.workerCalc.deductions,
          },
        })));
    totalsGroup.rows.push(
      // Expenses
      new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
        {
          irRow:
          {
            message: "SALAXY.REPORTS.ENUM.CalcGroup.expenses.label",
            total: calc.result.totals.totalExpenses - calc.result.workerCalc.prepaidExpenses,
          },
        })));
  }

  private static createEmployerReportRowGroups(report: CalculationReport, calc: Calculation, lang: Language): void {

    const paymentChannel: PaymentChannel = ((calc.info as any).paymentChannel || PaymentChannel.Undefined);
    const hasPaymentChannel = !(paymentChannel === PaymentChannel.Undefined ||
      paymentChannel === PaymentChannel.PalkkausCfaReference ||
      paymentChannel === PaymentChannel.PalkkausEcfaReference ||
      paymentChannel === PaymentChannel.PalkkausCfaFinvoice ||
      paymentChannel === PaymentChannel.PalkkausEcfaFinvoice ||
      paymentChannel === PaymentChannel.PalkkausCfaPaytrail ||
      paymentChannel === PaymentChannel.PalkkausEcfaPaytrail ||
      paymentChannel === PaymentChannel.HolviCfa ||
      paymentChannel === PaymentChannel.TalenomCfa);

    // Ordered set of groups
    const groups: CalcGroup[] = [

      CalcGroup.BaseSalary,
      CalcGroup.SalaryAdditions,
      CalcGroup.OtherNoPayment,
      CalcGroup.Expenses,
    ];

    const rowGroups = groups.map((group) => {
      const irRows = calc.result.irRows.filter((r) => r.calcData.grouping === group);
      return new CalculationReportRowGroup<CalculationReportRow<CalculationTableRowData>>(
        {
          grouping: group,
          rows: irRows.map((i) => new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData({ irRow: i }, lang))),
        });
    });

    // HACK: Move prepaid expenses to expenses group
    if (calc.result.workerCalc.prepaidExpenses !== 0) {
      const expensesGroup = rowGroups.find((g) => g.grouping === CalcGroup.Expenses);
      expensesGroup.rows.push(...calc.result.irRows.filter((r) =>
        this.getEnum<CalculationRowType>(r.data.calculationRowType) ===
        CalculationRowType.PrepaidExpenses).map((x) => {
          const row = new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData({ irRow: x }, lang));
          row.data.irRow.price = -1 * row.data.irRow.price;
          row.data.irRow.total = -1 * row.data.irRow.total;
          return row;
        }));
    }

    report.rowGroups.push(...rowGroups);

    const mandatorySideCostsGroup = new CalculationReportRowGroup<CalculationReportRow<CalculationTableRowData>>(
      {
        grouping: CalcGroup.Undefined,
        groupingTerm: "SALAXY.REPORTS.salary.heading.mandatorySideCosts",
      });

    const totalSalaryCostGroup = new CalculationReportRowGroup<CalculationReportRow<CalculationTableRowData>>(
      {
        grouping: CalcGroup.Undefined,
        groupingTerm: "SALAXY.REPORTS.salary.heading.salaryCost",
      });

    const finalCostGroup = new CalculationReportRowGroup<CalculationReportRow<CalculationTableRowData>>(
      {
        grouping: CalcGroup.Undefined,
        groupingTerm: "SALAXY.REPORTS.salary.heading.totalCost",
      });

    const finalCostGroupAfterTax = new CalculationReportRowGroup<CalculationReportRow<CalculationTableRowData>>(
      {
        grouping: CalcGroup.Undefined,
        groupingTerm: "SALAXY.REPORTS.salary.heading.totalCostAfterTax",
      });

    const totalsGroup = new CalculationReportRowGroup<CalculationReportRow<CalculationTableRowData>>(
      {
        grouping: CalcGroup.Totals,
        isHidden: hasPaymentChannel,
      });

    mandatorySideCostsGroup.rows.push(
      new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
        {
          irRow:
          {
            message: "SALAXY.REPORTS.salary.heading.pensionPayment",
            total: calc.result.employerCalc.pension,
            count: calc.result.totals.totalPensionInsuranceBase !== 0
              ? calc.result.employerCalc.pension /
              calc.result.totals.totalPensionInsuranceBase
              : 0,
            price: calc.result.totals.totalPensionInsuranceBase,
            unit: Unit.Percent,
          },
          comment: this.getPensionCompanyName(calc),
        })));

    mandatorySideCostsGroup.rows.push(
      new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
        {
          irRow:
          {
            message: "SALAXY.REPORTS.salary.heading.socialInsurancePayment",
            total: calc.result.employerCalc.socialSecurity,
            count: calc.result.totals.totalHealthInsuranceBase !== 0
              ? calc.result.employerCalc.socialSecurity /
              calc.result.totals.totalHealthInsuranceBase
              : 0,
            price: calc.result.totals.totalHealthInsuranceBase,
            unit: Unit.Percent,
          },
        })));

    mandatorySideCostsGroup.rows.push(
      new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
        {
          irRow:
          {
            message: "SALAXY.REPORTS.salary.heading.unemploymentInsurancePayment",
            total: calc.result.employerCalc.unemployment,
            count: calc.result.totals.totalUnemploymentInsuranceBase !== 0
              ? calc.result.employerCalc.unemployment /
              calc.result.totals.totalUnemploymentInsuranceBase
              : 0,
            price: calc.result.totals.totalUnemploymentInsuranceBase,
            unit: Unit.Percent,
          },
        })));

    totalSalaryCostGroup.rows.push(
      new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
        {
          irRow:
          {
            message: "SALAXY.REPORTS.salary.heading.grossSalaryLikeBaseSalaryAbove",
            total: calc.result.totals.totalGrossSalary,
            count: null,
            unit: Unit.Percent,
          },
        })));

    const mandatorySideCosts = new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
      {
        irRow:
        {
          message: "SALAXY.REPORTS.salary.heading.mandatorySideCosts",
          total: mandatorySideCostsGroup.total,
          count: null,
          unit: Unit.Percent,
        },
      }));

    totalSalaryCostGroup.rows.push(mandatorySideCosts);

    if (calc.result.employerCalc.palkkaus > 0) {
      const maxTotals = Math.max(calc.result.totals.totalSocialSecurityBase ?? 0, calc.result.totals.totalTaxable ?? 0);
      totalSalaryCostGroup.rows.push(new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
        {
          irRow:
          {
            message: "SALAXY.REPORTS.salary.heading.palkkausFee",
            total: calc.result.employerCalc.palkkaus,
            price: maxTotals,
            count: maxTotals !== 0 ? (this.calculatePriceExclVat(calc,calc.result.employerCalc.palkkaus)) / maxTotals : 0,
            unit: Unit.Percent,
          },
          comment:
            `(ALV ${this.displayVatPercent(calc)}, ${Numeric.formatPrice(this.calculateVat(calc, calc.result.employerCalc.palkkaus))})`,
        })));
    }
    if (calc.result.employerCalc.service > 0) {
      const serviceChargeRows = calc.result.irRows.filter((r) =>
      this.getEnum<CalculationRowType>(r.data.calculationRowType) === CalculationRowType.Service
      );
      if (serviceChargeRows.length > 0) {
        totalSalaryCostGroup.rows.push(...serviceChargeRows.map((x) => {
            const row = new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
                {
                irRow: x,
                comment:
              `(ALV ${this.displayVatPercent(calc)}, ${Numeric.formatPrice(this.calculateVat(calc, x.total))})`,
              }, lang));
              row.data.irRow.count = null;
              row.data.irRow.price = null;
            return row;
          }));
        }
        else
        {
          totalSalaryCostGroup.rows.push(new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
            {
              irRow: {
                message: Translations.get("SALAXY.ENUM.CalculationRowType.service.label", null, lang),
                total: calc.result.employerCalc.service,
                unit: Unit.Percent,
              },
              comment:`(ALV ${this.displayVatPercent(calc)}, ${Numeric.formatPrice(this.calculateVat(calc,calc.result.employerCalc.service))})`
            }
          )));
        }
    }

    const totalSalaryCost = new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
      {
        irRow:
        {
          message: "SALAXY.REPORTS.salary.heading.salaryCost",
          total: totalSalaryCostGroup.total,
          count: null,
          unit: Unit.Percent,
        },
      }));
    finalCostGroup.rows.push(totalSalaryCost);

    if (calc.result.totals.totalExpenses > 0) {
      finalCostGroup.rows.push(new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
        {
          irRow:
          {
            message: "SALAXY.REPORTS.ENUM.CalcGroup.expenses.label",
            total: calc.result.totals.totalExpenses - calc.result.workerCalc.prepaidExpenses,
            count: null,
            unit: Unit.Percent,
          },
        })));
    }

    const finalCost = new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
      {
        irRow:
        {
          message: finalCostGroup.rows.length > 1 ? "SALAXY.REPORTS.salary.heading.totalCost" : "SALAXY.REPORTS.salary.heading.salaryCost",
          total: finalCostGroup.total,
          count: null,
          unit: Unit.Percent,
        },
      }));
    totalsGroup.rows.push(finalCost);

    if (calc.salary.isHouseholdDeductible === true && calc.result.employerCalc.householdDeduction > 0) {

      finalCostGroupAfterTax.rows.push(finalCost);

      finalCostGroupAfterTax.rows.push(
        new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
          {
            irRow:
            {
              message: "SALAXY.REPORTS.salary.heading.householdDeduction",
              total: -1 * calc.result.employerCalc.householdDeduction,
              count: null,
              unit: Unit.Percent,
            },
          },
        )));
    }

    if (calc.result.employerCalc.deductionSalaryAdvance > 0) {
      totalsGroup.rows.push(
        new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
          {
            irRow:
            {
              message: "SALAXY.REPORTS.salary.heading.employerPaidSalaryAdvance",
              total: -1 * calc.result.employerCalc.deductionSalaryAdvance,
              count: null,
              unit: Unit.Percent,
            },
          })));
    }

    if (calc.result.employerCalc.deductionOtherDeductions > 0) {
      totalsGroup.rows.push(
        new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
          {
            irRow:
            {
              message: "SALAXY.REPORTS.salary.heading.employerPaidOtherDeduction",
              total: -1 * calc.result.employerCalc.deductionOtherDeductions,
              count: null,
              unit: Unit.Percent,
            },
          })));
    }

    if (calc.result.employerCalc.deductionForeclosure > 0) {
      totalsGroup.rows.push(
        new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
          {
            irRow:
            {
              message: "SALAXY.REPORTS.salary.heading.employerPaidForeclosure",
              total: -1 * calc.result.employerCalc.deductionForeclosure,
              count: null,
              unit: Unit.Percent,
            },
          })));
    }

    if (calc.result.employerCalc.deductionPensionSelfPayment > 0) {
      totalsGroup.rows.push(
        new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
          {
            irRow:
            {
              message: "SALAXY.REPORTS.salary.heading.employerPaidPension",
              total: -1 * calc.result.employerCalc.deductionPensionSelfPayment,
              count: calc.result.totals.totalPensionInsuranceBase !== 0
                ? calc.result.employerCalc.deductionPensionSelfPayment /
                calc.result.totals.totalPensionInsuranceBase
                : 0,
              price: calc.result.totals.totalPensionInsuranceBase,
              unit: Unit.Percent,
            },
          })));
    }
    if (calc.result.employerCalc.deductionUnemploymentSelfPayment > 0) {
      totalsGroup.rows.push(
        new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
          {
            irRow:
            {
              message: "SALAXY.REPORTS.salary.heading.employerPaidUnemploymentInsurance",
              total: -1 * calc.result.employerCalc.deductionUnemploymentSelfPayment,
              count: calc.result.totals.totalUnemploymentInsuranceBase !== 0
                ? calc.result.employerCalc.deductionUnemploymentSelfPayment /
                calc.result.totals.totalUnemploymentInsuranceBase
                : 0,
              price: calc.result.totals.totalUnemploymentInsuranceBase,
              unit: Unit.Percent,
            },
          })));
    }

    if (calc.result.employerCalc.deductionUnionPayment > 0) {
      totalsGroup.rows.push(
        new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
          {
            irRow:
            {
              message: "SALAXY.REPORTS.salary.heading.employerPaidUnion",
              total: -1 * calc.result.employerCalc.deductionUnionPayment,
              count: calc.result.totals.totalTaxable !== 0
                ? calc.result.employerCalc.deductionUnionPayment /
                calc.result.totals.totalTaxable
                : 0,
              price: calc.result.totals.totalTaxable,
              unit: Unit.Percent,
            },
          })));
    }

    if ((calc.result.employerCalc as any).deductionTaxAndSocialSecuritySelfPayment > 0) {
      totalsGroup.rows.push(
        new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
          {
            irRow:
            {
              message: "SALAXY.REPORTS.salary.heading.employerPaidTaxAndSocialSecurity",
              total: -1 * (calc.result.employerCalc as any).deductionTaxAndSocialSecuritySelfPayment,
              count: null,
              unit: Unit.Percent,
            },
          })));
    }

    if ((calc.result.employerCalc as any).deductionWorkerSelfPayment > 0) {
      totalsGroup.rows.push(
        new CalculationReportRow<CalculationTableRowData>(new CalculationTableRowData(
          {
            irRow:
            {
              message: "SALAXY.REPORTS.salary.heading.employerPaidWorker",
              total: -1 * (calc.result.employerCalc as any).deductionWorkerSelfPayment,
              count: null,
              unit: Unit.Percent,
            },
          })));
    }

    report.rowGroups.push(mandatorySideCostsGroup);
    report.rowGroups.push(totalSalaryCostGroup);

    if (finalCostGroup.rows.length > 1) {
      report.rowGroups.push(finalCostGroup);
    }
    if (finalCostGroupAfterTax.rows.length > 1) {
      report.rowGroups.push(finalCostGroupAfterTax);
    }
    report.rowGroups.push(totalsGroup);
  }

  private static createPaymentReportRowGroups(report: CalculationReport, calc: Calculation, lang: Language): void {

    const paymentChannel: PaymentChannel = ((calc.info as any).paymentChannel || PaymentChannel.Undefined);
    const hasPaymentChannel = !(paymentChannel === PaymentChannel.Undefined ||
      paymentChannel === PaymentChannel.PalkkausCfaReference ||
      paymentChannel === PaymentChannel.PalkkausEcfaReference ||
      paymentChannel === PaymentChannel.PalkkausCfaFinvoice ||
      paymentChannel === PaymentChannel.PalkkausEcfaFinvoice ||
      paymentChannel === PaymentChannel.PalkkausCfaPaytrail ||
      paymentChannel === PaymentChannel.PalkkausEcfaPaytrail ||
      paymentChannel === PaymentChannel.HolviCfa ||
      paymentChannel === PaymentChannel.TalenomCfa);

    // Groups

    // Payments
    const paymentsGroup = new CalculationReportRowGroup<CalculationReportRow<PaymentTableRowData>>(
      {
        grouping: CalcGroup.Undefined,
        groupingTerm: "SALAXY.REPORTS.salary.heading.paymentPart",
        params: { hideColumnHeaders: "" },
      });

    const finalCostGroupAfterTax = new CalculationReportRowGroup<CalculationReportRow<PaymentTableRowData>>(
      {
        grouping: CalcGroup.Undefined,
        groupingTerm: "SALAXY.REPORTS.salary.heading.totalCostAfterTax",
        params: { hideColumnHeaders: true },
      });

    // Totals
    const totalsGroup = new CalculationReportRowGroup<CalculationReportRow<PaymentTableRowData>>(
      {
        grouping: CalcGroup.Totals,
        params: { hideColumnHeaders: true },
        isHidden: hasPaymentChannel,
      });

    if (calc.result.totals.totalExpenses !== 0) {
      paymentsGroup.rows.push(
        new CalculationReportRow<PaymentTableRowData>(
          new PaymentTableRowData(
            {
              label: "SALAXY.REPORTS.ENUM.CalcGroup.expenses.label",
              total: calc.result.totals.totalExpenses,
              employerPercent: null,

              receiver: "SALAXY.REPORTS.salary.heading.worker",
              workerPercent: null,
            })));
    }

    if (calc.result.workerCalc.prepaidExpenses !== 0) {
      paymentsGroup.rows.push(
        new CalculationReportRow<PaymentTableRowData>(
          new PaymentTableRowData(
            {
              label: "SALAXY.REPORTS.salary.heading.prepaidExpenses",
              total: -1 * calc.result.workerCalc.prepaidExpenses,
              employerPercent: null,

              receiver: "SALAXY.REPORTS.salary.heading.worker",
              workerPercent: null,
            })));
    }

    if (calc.result.workerCalc.salaryAdvance !== 0) {
      paymentsGroup.rows.push(
        new CalculationReportRow<PaymentTableRowData>(
          new PaymentTableRowData(
            {
              label: "SALAXY.REPORTS.salary.heading.salaryAdvance",
              total: calc.result.workerCalc.salaryAdvance,
              baseAmount: null,
              employerPercent: null,

              receiver: "SALAXY.REPORTS.salary.heading.worker",
              workerPercent: null,
            })));
    }

    if (calc.result.workerCalc.otherDeductions  !== 0) {
      paymentsGroup.rows.push(
        new CalculationReportRow<PaymentTableRowData>(
          new PaymentTableRowData(
            {
              label: "SALAXY.REPORTS.salary.heading.otherDeductions",
              total: calc.result.workerCalc.otherDeductions,
              baseAmount: null,
              employerPercent: null,
              receiver: "SALAXY.REPORTS.salary.heading.worker",
            })));
    }

    if (calc.result.workerCalc.foreclosure !== 0) {
      paymentsGroup.rows.push(
        new CalculationReportRow<PaymentTableRowData>(
          new PaymentTableRowData(
            {
              label: "SALAXY.REPORTS.salary.heading.foreclosure",
              total: calc.result.workerCalc.foreclosure,
              employerPercent: null
              ,
              receiver: "SALAXY.REPORTS.salary.heading.foreclosureExecutor",
              workerPercent: null,
            })));
    }

    paymentsGroup.rows.push(
      new CalculationReportRow<PaymentTableRowData>(
        new PaymentTableRowData(
          {
            label: "SALAXY.REPORTS.salary.heading.withholdingTax",
            total: calc.result.workerCalc.tax,
            baseAmount: calc.result.totals.totalTaxable,
            employerPercent: null
            ,
            receiver: "SALAXY.REPORTS.salary.heading.taxman",
            workerPercent: calc.result.totals.totalTaxable !== 0 ? calc.result.workerCalc.tax / calc.result.totals.totalTaxable : 0,
          })));
    if ((calc.result.workerCalc as any).taxAtSource !== 0)
    {
          paymentsGroup.rows.push(
            new CalculationReportRow<PaymentTableRowData>(
              new PaymentTableRowData(
                {
                  label: "SALAXY.REPORTS.salary.heading.taxAtSource",
                  total: (calc.result.workerCalc as any).taxAtSource,
                  baseAmount: calc.result.totals.totalTaxable - (calc.result.totals as any).taxAtSourceDeduction,
                  employerPercent: null
                  ,
                  receiver: "SALAXY.REPORTS.salary.heading.taxman",
                  workerPercent: (calc.result.totals.totalTaxable - (calc.result.totals as any).taxAtSourceDeduction) !== 0 ? (calc.result.workerCalc as any).taxAtSource / (calc.result.totals.totalTaxable - (calc.result.totals as any).taxAtSourceDeduction) : 0,
                })));
    }

    paymentsGroup.rows.push(
      new CalculationReportRow<PaymentTableRowData>(
        new PaymentTableRowData(
          {
            label: "SALAXY.REPORTS.salary.heading.socialInsurancePayment",
            total: calc.result.employerCalc.socialSecurity,
            baseAmount: calc.result.totals.totalHealthInsuranceBase,
            employerPercent: calc.result.totals.totalHealthInsuranceBase !== 0 ? calc.result.employerCalc.socialSecurity / calc.result.totals.totalHealthInsuranceBase : null
            ,
            receiver: "SALAXY.REPORTS.salary.heading.taxman",
            workerPercent: null,
          })));
    if ((calc.result.workerCalc as any).socialSecurity !== 0) {
      paymentsGroup.rows.push(
        new CalculationReportRow<PaymentTableRowData>(
          new PaymentTableRowData(
            {
              label: "SALAXY.REPORTS.salary.heading.workersSocialInsurancePayment",
              total: (calc.result.workerCalc as any).socialSecurity,
              baseAmount: calc.result.totals.totalHealthInsuranceBase,
              workerPercent: calc.result.totals.totalHealthInsuranceBase !== 0 ? (calc.result.workerCalc as any).socialSecurity / calc.result.totals.totalHealthInsuranceBase : null
              ,
              receiver: "SALAXY.REPORTS.salary.heading.taxman",
              employerPercent: null,
            })));
    }

    paymentsGroup.rows.push(
      new CalculationReportRow<PaymentTableRowData>(
        new PaymentTableRowData(
          {
            label: "SALAXY.REPORTS.salary.heading.pensionPayment",
            total: calc.result.totals.pension,
            baseAmount: calc.result.totals.totalPensionInsuranceBase,
            employerPercent: calc.result.totals.totalPensionInsuranceBase !== 0 ? calc.result.employerCalc.pension / calc.result.totals.totalPensionInsuranceBase : null
            ,
            receiver: this.getPensionCompanyName(calc),
            workerPercent: calc.result.totals.totalPensionInsuranceBase !== 0 ? calc.result.workerCalc.pension / calc.result.totals.totalPensionInsuranceBase : null,
          })));

    paymentsGroup.rows.push(
      new CalculationReportRow<PaymentTableRowData>(
        new PaymentTableRowData(
          {
            label: "SALAXY.REPORTS.salary.heading.unemploymentInsurancePayment",
            total: calc.result.totals.unemployment,
            baseAmount: calc.result.totals.totalUnemploymentInsuranceBase,
            employerPercent: calc.result.totals.totalUnemploymentInsuranceBase !== 0 ? calc.result.employerCalc.unemployment / calc.result.totals.totalUnemploymentInsuranceBase : null
            ,
            receiver: "SALAXY.REPORTS.salary.heading.tvr",
            workerPercent: calc.result.totals.totalUnemploymentInsuranceBase !== 0 ? calc.result.workerCalc.unemploymentInsurance / calc.result.totals.totalUnemploymentInsuranceBase : null,
          })));

    if (calc.result.workerCalc.unionPayment !== 0) {

      const isRaksa = this.isRaksa(calc);

      paymentsGroup.rows.push(
        new CalculationReportRow<PaymentTableRowData>(
          new PaymentTableRowData(
            {
              label: "SALAXY.REPORTS.salary.heading.unionPayment",
              total: calc.result.workerCalc.unionPayment,
              baseAmount: calc.result.totals.totalTaxable,
              employerPercent: null
              ,
              receiver: isRaksa
                ? "SALAXY.REPORTS.salary.heading.raksa"
                : "SALAXY.REPORTS.salary.heading.union",
              workerPercent: calc.result.totals.totalTaxable !== 0
                ? calc.result.workerCalc.unionPayment / calc.result.totals.totalTaxable
                : null,
              comment: calc.result.employerCalc.deductionUnionPayment > 0 ? "SALAXY.REPORTS.salary.heading.workersEmployerHandledUnionPayment" : "",
            })));
    }

    if (calc.result.employerCalc.palkkaus !== 0) {
      paymentsGroup.rows.push(new CalculationReportRow<PaymentTableRowData>(
        new PaymentTableRowData(
          {
            label: "SALAXY.REPORTS.salary.heading.palkkausFee",
            total: calc.result.employerCalc.palkkaus,
            baseAmount: calc.result.totals.totalGrossSalary,
            employerPercent: calc.result.totals.totalGrossSalary !== 0
              ? (this.calculatePriceExclVat(calc,calc.result.employerCalc.palkkaus)) /
              calc.result.totals.totalGrossSalary
              : null,

            receiver: "SALAXY.REPORTS.salary.heading.palkkausfi",
            workerPercent: null,
            comment: `(ALV ${this.displayVatPercent(calc)}, ${Numeric.formatPrice(this.calculateVat(calc,calc.result.employerCalc.palkkaus))})`,
          })));
    }
    if (calc.result.employerCalc.service !== 0) {
      const serviceChargeRows = calc.result.irRows.filter((r) =>
        this.getEnum<CalculationRowType>(r.data.calculationRowType) === CalculationRowType.Service
      );
      if (serviceChargeRows.length > 0) {
      paymentsGroup.rows.push(...serviceChargeRows.map((x) => {
          const row = new CalculationReportRow<PaymentTableRowData>(new PaymentTableRowData(
            {
              label: x.message || Translations.get("SALAXY.ENUM.CalculationRowType.service.label", null, lang),
              total: x.total,
              employerPercent: null,
              receiver: "SALAXY.REPORTS.salary.heading.palkkausfi",
              workerPercent: null,
              comment: `(ALV ${this.displayVatPercent(calc)}, ${Numeric.formatPrice(this.calculateVat(calc, x.total))})`,
            }));
          return row;
        })
      );
      } else {
        paymentsGroup.rows.push(new CalculationReportRow<PaymentTableRowData>(new PaymentTableRowData(
            {
              label: Translations.get("SALAXY.ENUM.CalculationRowType.service.label", null, lang),
              total: calc.result.employerCalc.service,
              employerPercent: null,
              receiver: "SALAXY.REPORTS.salary.heading.palkkausfi",
              workerPercent: null,
              comment: `(ALV ${this.displayVatPercent(calc)}, ${Numeric.formatPrice(this.calculateVat(calc, calc.result.employerCalc.service))})`,
            })));
      }
    }

    paymentsGroup.rows.push(
      new CalculationReportRow<PaymentTableRowData>(
        new PaymentTableRowData(
          {
            label: "SALAXY.REPORTS.salary.heading.netSalary",
            total: calc.result.workerCalc.salaryPayment,
            employerPercent: null,

            receiver: "SALAXY.REPORTS.salary.heading.worker",
            workerPercent: null,
          })));

    if (calc.salary.isHouseholdDeductible === true && calc.result.employerCalc.householdDeduction !== 0) {

      finalCostGroupAfterTax.rows.push(new CalculationReportRow<PaymentTableRowData>(
        new PaymentTableRowData(
          {
            label: "SALAXY.REPORTS.salary.heading.totalCost",
            total: paymentsGroup.total,
            employerPercent: null,
          })));

      finalCostGroupAfterTax.rows.push(
        new CalculationReportRow<PaymentTableRowData>(
          new PaymentTableRowData(
            {
              label: "SALAXY.REPORTS.salary.heading.householdDeduction",
              total: -1 * calc.result.employerCalc.householdDeduction,
              baseAmount: null,
              employerPercent: null,
            })));
    }

    totalsGroup.rows.push(
      new CalculationReportRow<PaymentTableRowData>(
        new PaymentTableRowData(
          {
            label: "SALAXY.REPORTS.salary.heading.totalCost",
            total: paymentsGroup.total,
            baseAmount: null,
            employerPercent: null,
          })));

    if (calc.result.employerCalc.deductionSalaryAdvance !== 0) {
      totalsGroup.rows.push(
        new CalculationReportRow<PaymentTableRowData>(
          new PaymentTableRowData(
            {
              label: "SALAXY.REPORTS.salary.heading.employerPaidSalaryAdvance",
              total: -1 * calc.result.employerCalc.deductionSalaryAdvance,
              baseAmount: null,
              employerPercent: null,
            })));
    }

    if (calc.result.employerCalc.deductionOtherDeductions !== 0) {
      totalsGroup.rows.push(
        new CalculationReportRow<PaymentTableRowData>(
          new PaymentTableRowData(
            {
              label: "SALAXY.REPORTS.salary.heading.employerPaidOtherDeduction",
              total: -1 * calc.result.employerCalc.deductionOtherDeductions,
              baseAmount: null,
              employerPercent: null,
            })));
    }

    if (calc.result.employerCalc.deductionForeclosure !== 0) {
      totalsGroup.rows.push(
        new CalculationReportRow<PaymentTableRowData>(
          new PaymentTableRowData(
            {
              label: "SALAXY.REPORTS.salary.heading.employerPaidForeclosure",
              total: -1 * calc.result.employerCalc.deductionForeclosure,
              baseAmount: null,
              employerPercent: null,
            })));
    }

    if (calc.result.employerCalc.deductionPensionSelfPayment !== 0) {
      totalsGroup.rows.push(
        new CalculationReportRow<PaymentTableRowData>(
          new PaymentTableRowData(
            {
              label: "SALAXY.REPORTS.salary.heading.employerPaidPension",
              total: -1 * calc.result.employerCalc.deductionPensionSelfPayment,
              baseAmount: null,
              employerPercent: null,
            })));
    }

    if (calc.result.employerCalc.deductionUnemploymentSelfPayment !== 0) {
      totalsGroup.rows.push(
        new CalculationReportRow<PaymentTableRowData>(
          new PaymentTableRowData(
            {
              label: "SALAXY.REPORTS.salary.heading.employerPaidUnemploymentInsurance",
              total: -1 * calc.result.employerCalc.deductionUnemploymentSelfPayment,
              baseAmount: null,
              employerPercent: null,
            })));
    }

    if (calc.result.employerCalc.deductionUnionPayment !== 0) {
      totalsGroup.rows.push(
        new CalculationReportRow<PaymentTableRowData>(
          new PaymentTableRowData(
            {
              label: "SALAXY.REPORTS.salary.heading.employerPaidUnion",
              total: -1 * calc.result.employerCalc.deductionUnionPayment,
              baseAmount: null,
              employerPercent: null,
            })));
    }

    if ((calc.result.employerCalc as any).deductionTaxAndSocialSecuritySelfPayment !== 0) {
      totalsGroup.rows.push(
        new CalculationReportRow<PaymentTableRowData>(
          new PaymentTableRowData(
            {
              label: "SALAXY.REPORTS.salary.heading.employerPaidTaxAndSocialSecurity",
              total: -1 * (calc.result.employerCalc as any).deductionTaxAndSocialSecuritySelfPayment,
              baseAmount: null,
              employerPercent: null,
            })));
    }

    if ((calc.result.employerCalc as any).deductionWorkerSelfPayment !== 0) {
      totalsGroup.rows.push(
        new CalculationReportRow<PaymentTableRowData>(
          new PaymentTableRowData(
            {
              label: "SALAXY.REPORTS.salary.heading.employerPaidWorker",
              total: -1 * (calc.result.employerCalc as any).deductionWorkerSelfPayment,
              baseAmount: null,
              employerPercent: null,
            })));
    }

    report.rowGroups.push(paymentsGroup);
    if (finalCostGroupAfterTax.rows.length > 1) {
      report.rowGroups.push(finalCostGroupAfterTax);
    }
    report.rowGroups.push(totalsGroup);

  }

  private static createHeader(report: CalculationReport, calc: Calculation): void {
    // Employer
    report.header = {};
    report.header.employer = {};
    report.header.employer.avatar = calc.employer.avatar;

    // Address and official id
    report.header.employer.businessId = null; // N/A
    report.header.employer.contact = {
      street: calc.employer?.contact?.street,
      postalCode: calc.employer?.contact?.postalCode,
      city: calc.employer?.contact?.city
    }; // N/A

    // Worker
    report.header.worker = {};
    report.header.worker.avatar = calc.worker.avatar;
    report.header.worker.socialSecurityNumberValid = calc.worker.paymentData.socialSecurityNumberValid;
    report.header.worker.ibanNumber = calc.worker.paymentData.ibanNumber;
    report.header.worker.contact = {
      email: calc.worker.paymentData.email,
      telephone: calc.worker.paymentData.telephone,
      street: calc.worker.contact?.street,
      postalCode: calc.worker.contact?.postalCode,
      city: calc.worker.contact?.city
    };

    // Work/salary details
    report.header.numberOfDays = calc.framework.numberOfDays;
    report.header.paidAt = calc.workflow.paidAt;
    report.header.salaryDate = calc.workflow.salaryDate;
    report.header.requestedSalaryDate = calc.workflow.requestedSalaryDate;
    report.header.workDescription = calc.info.workDescription;
    report.header.salarySlipMessage = calc.info.salarySlipMessage;
    report.header.workStartDate = calc.info.workStartDate;
    report.header.workEndDate = calc.info.workEndDate;
    report.header.employmentPeriod = (calc.worktime || {}).employmentPeriod;
    report.header.workerHasNoTaxcard = calc.worker.tax.snapshot
      && calc.worker.tax.snapshot.kind === TaxcardKind.NoTaxCard;
    report.header.noWithholdingHousehold = calc.worker.tax.snapshot
      && calc.worker.tax.snapshot.kind === TaxcardKind.NoWithholdingHousehold;
    if (!report.header.workerHasNoTaxcard && calc.worker.tax.snapshot != null && calc.worker.tax.snapshot.kind !== TaxcardKind.Undefined) {
      const taxcard = calc.worker.tax.snapshot;
      report.header.taxcardTaxPercent = taxcard.taxPercent / 100.0;
      report.header.taxcardTaxPercent2 = taxcard.taxPercent2 / 100.0;
      report.header.taxcardIncomeLimit = taxcard.incomeLimit;
      report.header.taxcardCumulativeIncome = taxcard.cumulativeIncome;
    }

    report.header.isTaxAtSource = false;
    const isTaxAtSource = calc.result.irRows.flat().find(x => x.irData.code === 405 && (x.data?.calculationRowType.substr(0, 1).toLowerCase() + x.data?.calculationRowType.substr(1)) as CalculationRowType === CalculationRowType.TaxAtSource);
    if(isTaxAtSource) {
      report.header.isTaxAtSource = true;
      report.header.taxAtSourceDeductionPercent = calc.result.irRows.flat().find(x => x.irData.code === 404 && (x.data?.calculationRowType.substr(0, 1).toLowerCase() + x.data?.calculationRowType.substr(1)) as CalculationRowType === CalculationRowType.TaxAtSource)?.count ?? 0;
    }

    if (calc.result?.totals) {
      // Totals does not exist, if the calculation has not been recalculated.
      report.header.taxPercent = calc.result.totals.totalTaxable !== 0
        ? calc.result.workerCalc.tax / calc.result.totals.totalTaxable
        : 0;
    }
  }

  private static createSummaryHeader(report: CalculationSummaryReport, summarizedResults: CalculationResult, calcs: Calculation[]): void {
    // First create the basic calculation header...
    this.createHeader(report, calcs[0]);
    // ...then add the summary extensions
    report.header.calculationCount = calcs.length;
    // Filter out duplicate worker account entries before the worker count
    report.header.workerCount = calcs.map(v => v.worker.accountId).filter((v, i, a) => a.indexOf(v) === i).length;
    report.header.totalSalaryCost = summarizedResults.employerCalc.totalSalaryCost;
    report.header.totalGrossSalary = summarizedResults.totals.totalGrossSalary;
    report.header.totalWorkerPaymentLegacy = summarizedResults.workerCalc.totalWorkerPaymentLegacy;
  }

  private static createValidationMessages(report: CalculationReport, calc: Calculation): void {
    report.validationMessages = [];
    if (calc.result.workerCalc.fullSalaryAdvance > calc.result.workerCalc.salaryAdvance) {
      report.validationMessages.push(
        {
          text: "SALAXY.REPORTS.salary.text.salaryAdvanceError.html",
          parameters: {
            fullAmount:
              Numeric.formatNumber(calc.result.workerCalc.fullSalaryAdvance),
            differenceAmount:
              Numeric.formatNumber(calc.result.workerCalc.fullSalaryAdvance -
                calc.result.workerCalc.salaryAdvance),
          },
        });
    }
    if (calc.result.workerCalc.fullPension > calc.result.workerCalc.pension) {
      report.validationMessages.push({
        text: "SALAXY.REPORTS.salary.text.pensionError.html",
        parameters: {
          fullAmount:
            Numeric.formatNumber(calc.result.workerCalc.fullPension),
          differenceAmount:
            Numeric.formatNumber(calc.result.workerCalc.fullPension -
              calc.result.workerCalc.pension),
        },
      });
    }
    if (calc.result.workerCalc.fullUnemploymentInsurance > calc.result.workerCalc.unemploymentInsurance) {
      report.validationMessages.push({
        text: "SALAXY.REPORTS.salary.text.unemploymentInsuranceError.html",
        parameters: {
          fullAmount:
            Numeric.formatNumber(calc.result.workerCalc.fullUnemploymentInsurance),
          differenceAmount:
            Numeric.formatNumber(calc.result.workerCalc.fullUnemploymentInsurance -
              calc.result.workerCalc.unemploymentInsurance),
        },
      });
    }
    if (calc.result.workerCalc.fullUnionPayment > calc.result.workerCalc.unionPayment) {
      report.validationMessages.push({
        text: "SALAXY.REPORTS.salary.text.unionPaymentError.html",
        parameters: {
          fullAmount:
            Numeric.formatNumber(calc.result.workerCalc.fullUnionPayment),
          differenceAmount:
            Numeric.formatNumber(calc.result.workerCalc.fullUnionPayment -
              calc.result.workerCalc.unionPayment),
        },
      });
    }
    if (calc.result.workerCalc.fullOtherDeductions > calc.result.workerCalc.otherDeductions) {
      report.validationMessages.push({
        text: "SALAXY.REPORTS.salary.text.otherDeductionsError.html",
        parameters: {
          fullAmount:
            Numeric.formatNumber(calc.result.workerCalc.fullOtherDeductions),
          differenceAmount:
            Numeric.formatNumber(calc.result.workerCalc.fullOtherDeductions -
              calc.result.workerCalc.otherDeductions),
        },
      });
    }
    if (calc.result.workerCalc.fullPrepaidExpenses > calc.result.workerCalc.prepaidExpenses) {
      report.validationMessages.push({
        text: "SALAXY.REPORTS.salary.text.prepaidExpensesError.html",
        parameters: {
          fullAmount:
            Numeric.formatNumber(calc.result.workerCalc.fullPrepaidExpenses),
          differenceAmount:
            Numeric.formatNumber(calc.result.workerCalc.fullPrepaidExpenses -
              calc.result.workerCalc.prepaidExpenses),
        },
      });
    }
  }

  private static createEmployerReportNotes(report: CalculationReport, calc: Calculation): void {
    if (calc.framework.type === FrameworkAgreement.Construction && !calc.framework.isTesIncludedInSalary) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.framework",
          text: "SALAXY.REPORTS.salary.text.followsRaksaFramework",
          parameters:
          {
          },
        });
    }

    if (this.isCompensation(calc)) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.noPensionPayment",
          text: "SALAXY.REPORTS.salary.text.compensationPension",
          parameters:
          {
          },
        });
    } else if (calc.result.responsibilities.pensionCalculation === PensionCalculation.Entrepreneur ||
      calc.result.responsibilities.pensionCalculation === PensionCalculation.SmallEntrepreneur) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.noPensionPayment",
          text: "SALAXY.REPORTS.salary.text.entrepreneurPension",
          parameters:
          {
          },
        });
    } else if (calc.result.responsibilities.pensionCalculation === PensionCalculation.Farmer ||
      calc.result.responsibilities.pensionCalculation === PensionCalculation.SmallFarmer) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.noPensionPayment",
          text: "SALAXY.REPORTS.salary.text.farmerPension",
          parameters:
          {
          },
        });
    } else if (calc.result.responsibilities.pensionCalculation === PensionCalculation.Athlete) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.noPensionPayment",
          text: "SALAXY.REPORTS.salary.text.athletePension",
          parameters:
          {
          },
        });
    } else if (calc.result.totals.pension === 0) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.nullPensionPayment",
          text: "SALAXY.REPORTS.salary.text.nullPensionPayment",
          parameters:
          {
            amount:
              Numeric.formatNumber(this.getYearlyChangingNumbers(calc.result.responsibilities.calculationPaymentDate || Dates.getToday())?.sideCosts.tyelIncomeLimit),
          },
        });
    } /* else if (this.isPensionSelfHandling(calc) === true) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.employerHandledPensionPayment",
          text: "SALAXY.REPORTS.salary.text.employerHandledPensionPayment.html",
          parameters:
          {
            company:
              this.getPensionCompanyName(calc),
            contractNo:
              this.getPensionContractNumber(calc),
          },
        });
    } else if (this.isPensionContractDone(calc) === true) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.palkkausHandledPensionPayment",
          text: "SALAXY.REPORTS.salary.text.palkkausHandledPensionPayment.html",
          parameters:
          {
            company:
              this.getPensionCompanyName(calc),
            contractNo:
              this.getPensionContractNumber(calc),
          },
        });
    } else {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.palkkausHandledTempPensionPayment",
          text: "SALAXY.REPORTS.salary.text.palkkausHandledTempPensionPayment.html",
          parameters:
          {
            company:
              this.getPensionCompanyName(calc),
          },
        });
    }*/

    if (calc.result.totals.unemployment > 0) {

      if (calc.result.employerCalc.deductionUnemploymentSelfPayment > 0) {
        let text = "SALAXY.REPORTS.salary.text.employerHandledUnemploymentInsurancePayment";
        if (calc.result.responsibilities.pensionCalculation === PensionCalculation.PartialOwner) {
          text = "SALAXY.REPORTS.salary.text.employerHandledPartialOwnerUnemploymentInsurancePayment";

        }

        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.employerHandledUnemploymentInsurancePayment",
            text,
            parameters:
            {
              employerAmount:
                Numeric.formatNumber(calc.result.employerCalc.unemployment),
              workerAmount:
                Numeric.formatNumber(calc.result.workerCalc.unemploymentInsurance),
            },
          });
      } else {
        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.palkkausHandledUnemploymentInsurancePayment",
            text: "SALAXY.REPORTS.salary.text.palkkausHandledUnemploymentInsurancePayment",
            parameters:
            {
              employerAmount:
                Numeric.formatNumber(calc.result.employerCalc.unemployment),
              workerAmount:
                Numeric.formatNumber(calc.result.workerCalc.unemploymentInsurance),
            },
          });
      }
    } else {

      if (this.isCompensation(calc)) {
        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.noUnemploymentInsurancePayment",
            text: "SALAXY.REPORTS.salary.text.compensationUnemploymentInsurance",
            parameters:
            {
            },
          });
      } else if (calc.result.responsibilities.pensionCalculation === PensionCalculation.Entrepreneur ||
        calc.result.responsibilities.pensionCalculation === PensionCalculation.SmallEntrepreneur) {
        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.noUnemploymentInsurancePayment",
            text: "SALAXY.REPORTS.salary.text.entrepreneurUnemploymentInsurance",
            parameters:
            {
            },
          });
      } else if (calc.result.responsibilities.pensionCalculation === PensionCalculation.Farmer ||
        calc.result.responsibilities.pensionCalculation === PensionCalculation.SmallFarmer) {
        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.noUnemploymentInsurancePayment",
            text: "SALAXY.REPORTS.salary.text.farmerUnemploymentInsurance",
            parameters:
            {
            },
          });
      } else if (calc.result.responsibilities.pensionCalculation === PensionCalculation.Athlete) {
        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.noUnemploymentInsurancePayment",
            text: "SALAXY.REPORTS.salary.text.athleteUnemploymentInsurance",
            parameters:
            {
            },
          });
      } else {

        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.nullUnemploymentInsurancePayment",
            text: "SALAXY.REPORTS.salary.text.nullUnemploymentInsurancePayment",
            parameters:
            {
            },
          });
      }
    }

    if (calc.result.workerCalc.unionPayment > 0) {
      const unionPaymentPercent = calc.result.totals.totalTaxable !== 0
        ? calc.result.workerCalc.unionPayment / calc.result.totals.totalTaxable
        : 0;

      if (calc.result.employerCalc.deductionUnionPayment > 0) {
        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.employerHandledUnionPayment",
            text: "SALAXY.REPORTS.salary.text.employerHandledUnionPayment",
            parameters:
            {
              paymentRound:
                Numeric.formatNumber(calc.result.workerCalc.unionPayment),
              percent:
                Numeric.formatPercent(unionPaymentPercent * 100.0),
            },
          });
      } else {
        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.palkkausHandledUnionPayment",
            text: "SALAXY.REPORTS.salary.text.palkkausHandledUnionPayment",
            parameters:
            {
              paymentRound:
                Numeric.formatNumber(calc.result.workerCalc.unionPayment),
              percent:
                Numeric.formatPercent(unionPaymentPercent * 100.0),
            },
          });
      }
    }

    if (calc.salary.isHouseholdDeductible) {



      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.householdDeduction",
          text: "SALAXY.REPORTS.salary.text.householdDeductionVar",
          parameters:
          {
            ownRisk: Numeric.formatNumber(this.getHouseholdDeductionDeductible(calc),0),
            ownRiskDouble: Numeric.formatNumber(2 * this.getHouseholdDeductionDeductible(calc),0),
            max: Numeric.formatNumber(this.getHouseholdDeductionMaxAmount(calc),0),
            maxDouble: Numeric.formatNumber(2 * this.getHouseholdDeductionMaxAmount(calc),0),
          },
        });
    }

    if (this.isCompensation(calc) ||
      calc.result.responsibilities.pensionCalculation === PensionCalculation.Entrepreneur ||
      calc.result.responsibilities.pensionCalculation === PensionCalculation.Farmer ||
      calc.result.responsibilities.pensionCalculation === PensionCalculation.SmallEntrepreneur ||
      calc.result.responsibilities.pensionCalculation === PensionCalculation.SmallFarmer ||
      calc.result.responsibilities.pensionCalculation === PensionCalculation.Athlete ||
      calc.result.responsibilities.pensionCalculation === PensionCalculation.BoardRemuneration ||
      calc.result.responsibilities.pensionCalculation === PensionCalculation.Compensation) {

      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.noAccidentInsurancePayment",
          text: "SALAXY.REPORTS.salary.text.noAccidentInsurance",
          parameters:
          {
          },
        });
    } /* else {
      if (this.getInsuranceCompany(calc) != null &&
        this.getInsuranceCompany(calc) !== InsuranceCompany.None &&
        this.getInsuranceCompany(calc) !== InsuranceCompany.Pending) {

        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.accidentInsurancePayment",
            text: "SALAXY.REPORTS.salary.text.accidentInsurancePayment.html",
            parameters:
            {
              company:
                this.getInsuranceCompanyName(calc),
              contractNo:
                this.getInsuranceContractNumber(calc),
            },
          });
      } else {
        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.accidentInsurancePayment",
            text: "SALAXY.REPORTS.salary.text.missingAccidentInsurance",
            parameters:
            {
            },
          });
      }
    } */

    if (this.isCompensation(calc)) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.noSocialInsurancePayment",
          text: "SALAXY.REPORTS.salary.text.compensationSocialInsurance",
          parameters:
          {
          },
        });
    } else if (calc.result.totals.totalHealthInsuranceBase > 0) {

      if ((calc.result.employerCalc as any).deductionTaxAndSocialSecuritySelfPayment > 0) {
        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.socialInsurancePayment",
            text: "SALAXY.REPORTS.salary.text.socialSecuritySelfPayment",
            parameters:
            {
            },
          });
      } else {

        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.socialInsurancePayment",
            text: "SALAXY.REPORTS.salary.text.socialInsurancePayment",
            parameters:
              {},
          });
      }
    } else {

      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.nullSocialInsurancePayment",
          text: "SALAXY.REPORTS.salary.text.nullSocialInsurancePayment",
          parameters:
          {
          },
        });

    }

    report.notes.push(
      {
        heading: "SALAXY.REPORTS.salary.heading.incomesRegister",
        text: "SALAXY.REPORTS.salary.text.incomesRegisterNote",
        parameters:
        {
        },
      });

    if ((calc.result.employerCalc as any).deductionWorkerSelfPayment > 0) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.workerSelfPayment",
          text: "SALAXY.REPORTS.salary.text.workerSelfPayment",
          parameters:
          {
          },
        });
    }

    if ((calc.result.employerCalc as any).deductionTaxAndSocialSecuritySelfPayment > 0) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.taxSelfPayment",
          text: "SALAXY.REPORTS.salary.text.taxSelfPayment",
          parameters:
          {
          },
        });
    }
  }

  private static createSalarySlipNotes(report: CalculationReport, calc: Calculation): void {
    if (calc.framework.type === FrameworkAgreement.Construction && !calc.framework.isTesIncludedInSalary) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.framework",
          text: "SALAXY.REPORTS.salary.text.followsRaksaFramework",
          parameters:
          {
          },
        });
    }

    if (this.isCompensation(calc)) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.noPensionPayment",
          text: "SALAXY.REPORTS.salary.text.compensationPension",
          parameters:
          {
          },
        });
    } else if (calc.result.responsibilities.pensionCalculation === PensionCalculation.Entrepreneur ||
      calc.result.responsibilities.pensionCalculation === PensionCalculation.SmallEntrepreneur) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.noPensionPayment",
          text: "SALAXY.REPORTS.salary.text.entrepreneurPension",
          parameters:
          {
          },
        });
    } else if (calc.result.responsibilities.pensionCalculation === PensionCalculation.Farmer ||
      calc.result.responsibilities.pensionCalculation === PensionCalculation.SmallFarmer) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.noPensionPayment",
          text: "SALAXY.REPORTS.salary.text.farmerPension",
          parameters:
          {
          },
        });
    } else if (calc.result.responsibilities.pensionCalculation === PensionCalculation.Athlete) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.noPensionPayment",
          text: "SALAXY.REPORTS.salary.text.athletePension",
          parameters:
          {
          },
        });
    } else if (calc.result.totals.pension === 0) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.nullPensionPayment",
          text: "SALAXY.REPORTS.salary.text.nullPensionPayment",
          parameters:
          {
            amount:
              Numeric.formatNumber(this.getYearlyChangingNumbers(calc.result.responsibilities.calculationPaymentDate || Dates.getToday())?.sideCosts.tyelIncomeLimit),
          },
        });
    } /* else if (this.isPensionSelfHandling(calc) === true) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.workersEmployerHandledPensionPayment",
          text: "SALAXY.REPORTS.salary.text.workersEmployerHandledPensionPayment.html",
          parameters:
          {
            company:
              this.getPensionCompanyName(calc),
            contractNo:
              this.getPensionContractNumber(calc),
          },
        });
    } else if (this.isPensionContractDone(calc) === true) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.workersPalkkausHandledPensionPayment",
          text: "SALAXY.REPORTS.salary.text.workersPalkkausHandledPensionPayment.html",
          parameters:
          {
            company:
              this.getPensionCompanyName(calc),
            contractNo:
              this.getPensionContractNumber(calc),
          },
        });
    } else {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.palkkausHandledTempPensionPayment",
          text: "SALAXY.REPORTS.salary.text.palkkausHandledTempPensionPayment.html",
          parameters:
          {
            company:
              this.getPensionCompanyName(calc),
          },
        });
    } */

    if (calc.result.totals.unemployment > 0) {

      if (calc.result.employerCalc.deductionUnemploymentSelfPayment > 0) {
        let text = "SALAXY.REPORTS.salary.text.workersEmployerHandledUnemploymentInsurancePayment";
        if (calc.result.responsibilities.pensionCalculation === PensionCalculation.PartialOwner) {
          text = "SALAXY.REPORTS.salary.text.workersEmployerHandledPartialOwnerUnemploymentInsurancePayment";

        }

        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.workersEmployerHandledUnemploymentInsurancePayment",
            text,
            parameters:
            {
            },
          });
      } else {
        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.workersPalkkausHandledUnemploymentInsurancePayment",
            text: "SALAXY.REPORTS.salary.text.workersPalkkausHandledUnemploymentInsurancePayment",
            parameters:
            {
            },
          });
      }
    } else {

      if (this.isCompensation(calc)) {
        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.noUnemploymentInsurancePayment",
            text: "SALAXY.REPORTS.salary.text.compensationUnemploymentInsurance",
            parameters:
            {
            },
          });
      } else if (calc.result.responsibilities.pensionCalculation === PensionCalculation.Entrepreneur ||
        calc.result.responsibilities.pensionCalculation === PensionCalculation.SmallEntrepreneur) {
        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.noUnemploymentInsurancePayment",
            text: "SALAXY.REPORTS.salary.text.entrepreneurUnemploymentInsurance",
            parameters:
            {
            },
          });
      } else if (calc.result.responsibilities.pensionCalculation === PensionCalculation.Farmer ||
        calc.result.responsibilities.pensionCalculation === PensionCalculation.SmallFarmer) {
        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.noUnemploymentInsurancePayment",
            text: "SALAXY.REPORTS.salary.text.farmerUnemploymentInsurance",
            parameters:
            {
            },
          });
      } else if (calc.result.responsibilities.pensionCalculation === PensionCalculation.Athlete) {
        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.noUnemploymentInsurancePayment",
            text: "SALAXY.REPORTS.salary.text.athleteUnemploymentInsurance",
            parameters:
            {
            },
          });
      } else {

        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.nullUnemploymentInsurancePayment",
            text: "SALAXY.REPORTS.salary.text.nullUnemploymentInsurancePayment",
            parameters:
            {
            },
          });
      }
    }

    if (calc.result.workerCalc.unionPayment > 0) {
      const unionPaymentPercent = calc.result.totals.totalTaxable !== 0
        ? calc.result.workerCalc.unionPayment / calc.result.totals.totalTaxable
        : 0;

      if (calc.result.employerCalc.deductionUnionPayment > 0) {
        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.employerHandledUnionPayment",
            text: "SALAXY.REPORTS.salary.text.employerHandledUnionPayment",
            parameters:
            {
              paymentRound:
                Numeric.formatNumber(calc.result.workerCalc.unionPayment),
              percent:
                Numeric.formatPercent(unionPaymentPercent * 100.0),
            },
          });
      } else {
        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.palkkausHandledUnionPayment",
            text: "SALAXY.REPORTS.salary.text.palkkausHandledUnionPayment",
            parameters:
            {
              paymentRound:
                Numeric.formatNumber(calc.result.workerCalc.unionPayment),
              percent:
                Numeric.formatPercent(unionPaymentPercent * 100.0),
            },
          });
      }
    }
    if (calc.salary.isHouseholdDeductible) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.householdDeduction",
          text: "SALAXY.REPORTS.salary.text.householdDeductionVar",
          parameters:
          {
            ownRisk: Numeric.formatNumber(this.getHouseholdDeductionDeductible(calc), 0),
            ownRiskDouble: Numeric.formatNumber(2 * this.getHouseholdDeductionDeductible(calc), 0),
            max: Numeric.formatNumber(this.getHouseholdDeductionMaxAmount(calc), 0),
            maxDouble: Numeric.formatNumber(2 * this.getHouseholdDeductionMaxAmount(calc), 0),
          },
        });
    }

    if (this.isCompensation(calc) ||
      calc.result.responsibilities.pensionCalculation === PensionCalculation.Entrepreneur ||
      calc.result.responsibilities.pensionCalculation === PensionCalculation.Farmer ||
      calc.result.responsibilities.pensionCalculation === PensionCalculation.SmallEntrepreneur ||
      calc.result.responsibilities.pensionCalculation === PensionCalculation.SmallFarmer ||
      calc.result.responsibilities.pensionCalculation === PensionCalculation.Athlete ||
      calc.result.responsibilities.pensionCalculation === PensionCalculation.BoardRemuneration ||
      calc.result.responsibilities.pensionCalculation === PensionCalculation.Compensation) {

      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.noAccidentInsurancePayment",
          text: "SALAXY.REPORTS.salary.text.noAccidentInsurance",
          parameters:
          {
          },
        });
    } /* else {
      if (this.getInsuranceCompany(calc) != null &&
        this.getInsuranceCompany(calc) !== InsuranceCompany.None &&
        this.getInsuranceCompany(calc) !== InsuranceCompany.Pending) {

        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.workersAccidentInsurancePayment",
            text: "SALAXY.REPORTS.salary.text.workersAccidentInsurancePayment.html",
            parameters:
            {
              company:
                this.getInsuranceCompanyName(calc),
              contractNo:
                this.getInsuranceContractNumber(calc),
            },
          });
      } else {
        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.workersAccidentInsurancePayment",
            text: "SALAXY.REPORTS.salary.text.workersMissingAccidentInsurance",
            parameters:
            {
            },
          });
      }
    } */

    if ((calc.result.workerCalc as any).socialSecurity > 0) {
      // add notes for workers explicit social security payment
    }
    else
    {
      if (calc.result.totals.totalHealthInsuranceBase > 0) {
        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.workersSocialInsurancePayment",
            text: "SALAXY.REPORTS.salary.text.workersSocialInsurancePayment",
            parameters:
            {
              year:
                Dates.getYear(calc.result.responsibilities.calculationPaymentDate || Dates.getToday()),
              percent:
                Numeric.formatNumber(this.getYearlyChangingNumbers(calc.result.responsibilities.calculationPaymentDate || Dates.getToday())?.sideCosts.illnessInsurancePercent * 100.0),
              totalBaseRound:
                Numeric.formatNumber(calc.result.totals.totalHealthInsuranceBase),
              amountRound:
                Numeric.formatNumber(calc.result.totals.totalHealthInsuranceBase * this.getYearlyChangingNumbers(calc.result.responsibilities.calculationPaymentDate || Dates.getToday())?.sideCosts.illnessInsurancePercent),
            },
          });
      } else {
        report.notes.push(
          {
            heading: "SALAXY.REPORTS.salary.heading.workersNullSocialInsurancePayment",
            text: "SALAXY.REPORTS.salary.text.workersNullSocialInsurancePayment",
            parameters:
            {
            },
          });
      }
    }

    report.notes.push(
      {
        heading: "SALAXY.REPORTS.salary.heading.incomesRegister",
        text: "SALAXY.REPORTS.salary.text.incomesRegisterNote",
        parameters:
        {
        },
      });

    if ((calc.result.employerCalc as any).deductionWorkerSelfPayment > 0) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.workerSelfPayment",
          text: "SALAXY.REPORTS.salary.text.workerSelfPayment",
          parameters:
          {
          },
        });
    }

    if ((calc.result.employerCalc as any).deductionTaxAndSocialSecuritySelfPayment > 0) {
      report.notes.push(
        {
          heading: "SALAXY.REPORTS.salary.heading.taxSelfPayment",
          text: "SALAXY.REPORTS.salary.text.taxSelfPayment",
          parameters:
          {
          },
        });
    }

  }

  private static createCumulativeData(report: CalculationReport, calc: Calculation): void {
    const cumulativeDataDic = (calc as CumulativeCalculation).cumulative;
    if (cumulativeDataDic) {
      report.cumulative = [];

      const periodKeys = Object.keys(cumulativeDataDic);

      for (const periodKey of periodKeys) {
        let i = 0;
        const cumulativeData = cumulativeDataDic[periodKey];
        const dataKeys = Object.keys(cumulativeData.data);
        for (const dataKey of dataKeys) {
          const row: CalculationReportSimpleRow = {
            key: dataKey,
            label: cumulativeData.labels[dataKey],
            total: cumulativeData.data[dataKey],
            data: {
              [periodKey]: true,
              [i++ % 2 === 0 ? "left" : "right"]: true,
              unit: {
                [(cumulativeData.units ?? {})[dataKey] ?? "none"] : true,
              },
            },
          };
          report.cumulative.push(row);
        }
      }
    } else {
      report.cumulative = null;
    }
  }

  private static getPensionCompanyName(calc: Calculation): string {
    if (calc.result.responsibilities) {
      const pensionCompany = calc.result.responsibilities.pensionCompany;
      if (pensionCompany) {
        return "SALAXY.ENUM.PensionCompany." + pensionCompany + ".label";
      }
      return "";
    }
  }
  private static isCompensation(calc: Calculation): boolean {
    // check all rows
    return calc.result.irRows.some((r) => r.irData.code === 336); // NonWageCompensationForWork
  }

  private static getEnum<T>(value: any): T {
    if (!value) {
      return value;
    }
    let str = `${value as string}`;
    str = str.charAt(0).toLowerCase() + str.slice(1);
    return str as any as T;
  }

  private static isRaksa(calc: Calculation): boolean {
    let isRaksa = false;
    if ((calc.framework as any).unionPayment === UnionPaymentType.RaksaNormal ||
      (calc.framework as any).unionPayment === UnionPaymentType.RaksaUnemploymentOnly) {
      isRaksa = true;
    } else {
      for (const row of calc.rows) {
        if (row.rowType === CalculationRowType.UnionPayment) {
          const type = this.getEnum<UnionPaymentKind>(row.data.kind);
          if (type === UnionPaymentKind.RaksaNormal || type === UnionPaymentKind.RaksaUnemploymentOnly) {
            isRaksa = true;
            break;
          }
        }
      }
    }

    return isRaksa;
  }

  private static calculateCalculationsResultsSummary(calculations: Calculation[]): CalculationResult {
    // TODO: Maybe we should also summarize rest of the results: irRows, responsibilities, rows, validation...
    return calculations
      .map(v => v.result)
      .reduce(
        (prev, cur) => {
          return {
            employerCalc: {
              allSideCosts: this.summarizeField(prev, cur, "employerCalc.allSideCosts"),
              deductionForeclosure: this.summarizeField(prev, cur, "employerCalc.deductionForeclosure"),
              deductionOtherDeductions: this.summarizeField(prev, cur, "employerCalc.deductionOtherDeductions"),
              deductionPensionSelfPayment: this.summarizeField(prev, cur, "employerCalc.deductionPensionSelfPayment"),
              deductionSalaryAdvance: this.summarizeField(prev, cur, "employerCalc.deductionSalaryAdvance"),
              deductionTaxAndSocialSecuritySelfPayment: this.summarizeField(prev, cur, "employerCalc.deductionTaxAndSocialSecuritySelfPayment"),
              deductionUnemploymentSelfPayment: this.summarizeField(prev, cur, "employerCalc.deductionUnemploymentSelfPayment"),
              deductionUnionPayment: this.summarizeField(prev, cur, "employerCalc.deductionUnionPayment"),
              deductionWorkerSelfPayment: this.summarizeField(prev, cur, "employerCalc.deductionWorkerSelfPayment"),
              finalCost: this.summarizeField(prev, cur, "employerCalc.finalCost"),
              foreclosureByPalkkaus: this.summarizeField(prev, cur, "employerCalc.foreclosureByPalkkaus"),
              householdDeduction: this.summarizeField(prev, cur, "employerCalc.householdDeduction"),
              mandatorySideCosts: this.summarizeField(prev, cur, "employerCalc.mandatorySideCosts"),
              palkkaus: this.summarizeField(prev, cur, "employerCalc.palkkaus"),
              service: this.summarizeField(prev, cur, "employerCalc.service"),
              pension: this.summarizeField(prev, cur, "employerCalc.pension"),
              socialSecurity: this.summarizeField(prev, cur, "employerCalc.socialSecurity"),
              totalDeductions: this.summarizeField(prev, cur, "employerCalc.totalDeductions"),
              totalPayment: this.summarizeField(prev, cur, "employerCalc.totalPayment"),
              totalPaymentLegacy: this.summarizeField(prev, cur, "employerCalc.totalPaymentLegacy"),
              totalSalaryCost: this.summarizeField(prev, cur, "employerCalc.totalSalaryCost"),
              unemployment: this.summarizeField(prev, cur, "employerCalc.unemployment"),
            },
            totals: {
              pension: this.summarizeField(prev, cur, "totals.pension"),
              total: this.summarizeField(prev, cur, "totals.total"),
              totalAccidentInsuranceBase: this.summarizeField(prev, cur, "totals.totalAccidentInsuranceBase"),
              totalExpenses: this.summarizeField(prev, cur, "totals.totalExpenses"),
              totalBaseSalary: this.summarizeField(prev, cur, "totalstotalBaseSalary."),
              totalGrossSalary: this.summarizeField(prev, cur, "totals.totalGrossSalary"),
              totalHealthInsuranceBase: this.summarizeField(prev, cur, "totals.totalHealthInsuranceBase"),
              totalPayable: this.summarizeField(prev, cur, "totals.totalPayable"),
              totalPensionInsuranceBase: this.summarizeField(prev, cur, "totals.totalPensionInsuranceBase"),
              totalSocialSecurityBase: this.summarizeField(prev, cur, "totals.totalSocialSecurityBase"),
              totalTaxable: this.summarizeField(prev, cur, "totals.totalTaxable"),
              totalUnemploymentInsuranceBase: this.summarizeField(prev, cur, "totals.totalUnemploymentInsuranceBase"),
              unemployment: this.summarizeField(prev, cur, "totals.unemployment"),
            },
            workerCalc: {
              benefits: this.summarizeField(prev, cur, "workerCalc.benefits"),
              deductions: this.summarizeField(prev, cur, "workerCalc.deductions"),
              foreclosure: this.summarizeField(prev, cur, "workerCalc.foreclosure"),
              fullOtherDeductions: this.summarizeField(prev, cur, "workerCalc.fullOtherDeductions"),
              fullPension: this.summarizeField(prev, cur, "workerCalc.fullPension"),
              fullPrepaidExpenses: this.summarizeField(prev, cur, "workerCalc.fullPrepaidExpenses"),
              fullSalaryAdvance: this.summarizeField(prev, cur, "workerCalc.fullSalaryAdvance"),
              fullTax: this.summarizeField(prev, cur, "workerCalc.fullTax"),
              fullUnemploymentInsurance: this.summarizeField(prev, cur, "workerCalc.fullUnemploymentInsurance"),
              fullUnionPayment: this.summarizeField(prev, cur, "workerCalc.fullUnionPayment"),
              otherDeductions: this.summarizeField(prev, cur, "workerCalc.otherDeductions"),
              pension: this.summarizeField(prev, cur, "workerCalc.pension"),
              prepaidExpenses: this.summarizeField(prev, cur, "workerCalc.prepaidExpenses"),
              salaryAdvance: this.summarizeField(prev, cur, "workerCalc.salaryAdvance"),
              salaryAfterTax: this.summarizeField(prev, cur, "workerCalc.salaryAfterTax"),
              salaryAfterTaxAndForeclosure: this.summarizeField(prev, cur, "workerCalc.salaryAfterTaxAndForeclosure"),
              salaryPayment: this.summarizeField(prev, cur, "workerCalc.salaryPayment"),
              tax: this.summarizeField(prev, cur, "workerCalc.tax"),
              totalWorkerPayment: this.summarizeField(prev, cur, "workerCalc."),
              totalWorkerPaymentLegacy: this.summarizeField(prev, cur, "workerCalc.totalWorkerPaymentLegacy"),
              unemploymentInsurance: this.summarizeField(prev, cur, "workerCalc.unemploymentInsurance"),
              unionPayment: this.summarizeField(prev, cur, "workerCalc.unionPayment"),
              workerSideCosts: this.summarizeField(prev, cur, "workerCalc.workerSideCosts"),
            },
          };
        },
        {}
      );
  }

  private static summarizeField(obj1: any, obj2: any, nestedField: string): number {
    nestedField.split(".").forEach(v => {
      obj1 = obj1[v] || 0;
      obj2 = obj2[v] || 0;
    });
    return obj1 + obj2;
  }

  private static getYearlyChangingNumbers(forYear: DatelyObject): YearlyChangingNumbers {
    // Must support old calculations before year 2018
    try {
      return Years.getYearlyChangingNumbers(forYear);
    } catch {
      return null;
    }
  }

  /** Current household deduction max amount. */
  private static getHouseholdDeductionMaxAmount(calc: Calculation) {
    const category = this.getTaxDeductionWorkCategories(calc);
    const sideCosts = this.getYearlyChangingNumbers(calc.result?.responsibilities?.calculationPaymentDate || Dates.getToday())?.sideCosts;

    if (this.getHouseholdDeductionCategoriesExtended(sideCosts).includes(category)) {
      return sideCosts?.householdDeductionMaxAmountExtended;
    }
    return sideCosts?.householdDeductionMaxAmount;
  }

  private static getHouseholdDeductionDeductible(calc: Calculation) {
    const sideCosts = this.getYearlyChangingNumbers(calc.result?.responsibilities?.calculationPaymentDate || Dates.getToday())?.sideCosts;
    return sideCosts?.householdDeductionDeductible;
  }

  private static getTaxDeductionWorkCategories(calc: Calculation) : TaxDeductionWorkCategories {
    return calc.usecase?.data?.taxDeductionCategories ?? TaxDeductionWorkCategories.None;
  }

  private static getHouseholdDeductionCategoriesExtended(sideCosts: YearlySideCosts): TaxDeductionWorkCategories[] {
    return sideCosts?.householdDeductionCategoriesExtended ?? [];
  }

  private static getPalkkausVatPercent(calc: Calculation): number {
    return this.getYearlyChangingNumbers(calc.workflow?.paidAt || calc.result?.responsibilities?.calculationPaymentDate || Dates.getToday())?.sideCosts.palkkausVatPercent;
  }

  private static displayVatPercent(calc: Calculation): string {
    const vat = this.getPalkkausVatPercent(calc) * 100;
    return `${vat}%`.replace(".", "," );
  }

  private static calculateVat(calc: Calculation, priceInclVat: number): number {
    const vat = this.getPalkkausVatPercent(calc);
    return priceInclVat * vat / (1 + vat);
  }
  private static calculatePriceExclVat(calc: Calculation, priceInclVat: number): number {
    const vat = this.getPalkkausVatPercent(calc);
    return priceInclVat / (1 + vat);
  }
}
